import { kebabCase } from "lodash";

export default {
	model: {
		prop: "value",
		event: "selected-change"
	},

	inheritAttrs: false,

	props: {
		/**
		 * The id of the Select. This prop is mostly used for accessiblity purposes so it is required. Like the HTML id attribute this should be unique to the page.
		 */
		id: {
			type: String,
			required: true
		},

		/**
		 * Placeholder text to show when there is not selected value. Used in conjunction with the allowEmpty prop.
		 */
		placeholder: {
			type: String,
			default: ""
		},

		/**
		 * Allows the Select to have an empty selected state ie: no selected value.
		 */
		allowEmpty: {
			type: Boolean,
			default: false
		},

		/**
		 * The label of the  Select. This field is used for accessiblity purposes so it is required.
		 */
		label: {
			type: String,
			required: true
		},

		/**
		 * If you want to show the static label as the button text no matter what the selection is, set this value to `true`. Default value is `false`.
		 */
		showLabelInButton: {
			type: Boolean,
			default: false
		},

		/**
		 * The default color scheme for this is white background with dark text. If you want this to use inverted colors, set this value to `inverted`.
		 *
		 * @values default, inverted
		 */
		colorScheme: {
			type: String,
			default: "default"
		},

		/**
		 * You can setup 2-way data binding to control and react to the selected value change by using v-model. If you are not familar with v-model see documentation [here](https://vuejs.org/v2/guide/forms.html). When allowEmpty is true passing an empty string will clear any selected value.
		 */
		value: {
			type: [String, Number],
			default: ""
		},

		/**
		 * The selected index of the Select. Use this the prop to set the desired selected option by index. This is an alternative to using v-model which uses the select option value. Use this prop if you would like to control the Select with an index instead of an option value via v-model. When allowEmpty is true passing -1 will clear any selected value.
		 */
		selected: {
			type: Number,
			default: -1
		},

		/**
		 * The options of the Secondary Select. The only requirement is that each option object contain a value and label property. Additional optional properties include 1) A 'disabled' property is available that disables the list item when true, 2) A 'buttonLabel' property is available that allows finer grain control over the label rendered in the select button. Accepts HTML. If not defined 'label' is used, and 3) A 'selectLabel' property is available that allows finer grain control over the label rendered in a select option. Accepts HTML. If not defined 'label' is used. Other properties will be ignored by the component.
		 */
		options: {
			type: [Array, Object],
			default: () => {
				return [];
			}
		},

		/**
		 * If the options list has only 1 option, setting this value to `false` will disable the dropdown list from appearing.
		 */
		showOnOneOption: {
			type: Boolean,
			default: true
		}
	},

	computed: {
		labelId() {
			return `${this.id}-select-label`;
		},

		buttonId() {
			return `${this.id}-select-button`;
		},

		listId() {
			return `${this.id}-list`;
		},

		ariaLabelledBy() {
			return `${this.buttonId} ${this.labelId}`;
		}
	},

	methods: {
		getButtonLabel(option) {
			return this.showLabelInButton
				? this.label
				: this.getOptionButtonLabel(option) || !this.allowEmpty
				? this.getOptionButtonLabel(option)
				: this.placeholder;
		},

		getOptionButtonLabel(option) {
			return option.buttonLabel ? option.buttonLabel : option.label;
		},

		getOptionSelectLabel(option) {
			return option.selectLabel ? option.selectLabel : option.label;
		},

		groupLabelId(group) {
			return `${this.id}-opt-group-${kebabCase(group)}`;
		}
	}
};
